import { validate as validateUuid } from 'uuid';
import {
	ChatRef,
	DesignSystem,
	DesignSystemVersion,
	DsMode,
	SceneComment,
	SceneDsLink,
} from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '@constants';
import {
	clearActiveScene,
	setSceneLoadError,
	updateSceneChatRefs,
	updateSceneComments,
	resolveOptimisticallyUpdatedSceneComments,
	updateActiveSceneDsLink,
	updateActiveSceneDesignSystemModes,
} from '../redux/db/actions';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

interface ScenePollResponse {
	chatRefs: ChatRef[];
	comments?: SceneComment[];
	sceneDsLink?: {
		designSystem: DesignSystem;
		linkedVersion: DesignSystemVersion;
		latestPublishedVersion: DesignSystemVersion;
		hasDraft: boolean;
	} | null;
	dsModes?: DsMode[];
	timestamp: string;
}

// Processes date fields on a DesignSystemVersion from JSON
const processVersion = (v: DesignSystemVersion): DesignSystemVersion => ({
	...v,
	createdAt: new Date(v.createdAt),
	updatedAt: new Date(v.updatedAt),
});

export class ScenePollingStream extends PollingStream<ScenePollResponse> {
	constructor(activeSceneId: string) {
		// Validate UUIDs before constructing URL
		if (!validateUuid(activeSceneId)) {
			throw new Error(`Invalid scene ID: ${activeSceneId}`);
		}

		const url = VITE_FORGE_URL + `/poll/scene/${activeSceneId}`;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(setSceneLoadError('You do not have access to this project'));
				store.dispatch(clearActiveScene());
			} else {
				console.error(error);
			}
		};

		super(url, onError, 'activeScene.chats');
	}

	extractData(response: any): ScenePollResponse[] {
		return [response];
	}

	onData(responses: ScenePollResponse[]) {
		const response = responses.length > 0 ? responses[0] : null;
		if (!response) {
			store.dispatch(clearActiveScene());
			return;
		}

		// Update chat refs
		const processedChatRefs: ChatRef[] = (response.chatRefs || []).map((ref) => ({
			...ref,
			updatedAt: new Date(ref.updatedAt),
		}));
		store.dispatch(updateSceneChatRefs(processedChatRefs));

		// Update scene comments
		const processedComments: SceneComment[] = (response.comments || []).map((c) => ({
			...c,
			createdAt: new Date(c.createdAt),
			updatedAt: c.updatedAt ? new Date(c.updatedAt) : undefined,
			deletedAt: c.deletedAt ? new Date(c.deletedAt) : null,
		}));
		const commentWriteIds = processedComments
			.map((c) => c.writeId)
			.filter((writeId): writeId is string => !!writeId);
		if (commentWriteIds.length > 0) {
			store.dispatch(resolveOptimisticallyUpdatedSceneComments(commentWriteIds));
		}
		store.dispatch(updateSceneComments(processedComments));

		// Update design system link
		if (response.sceneDsLink) {
			const raw = response.sceneDsLink;
			const dsLink: SceneDsLink = {
				designSystem: {
					...raw.designSystem,
					createdAt: new Date(raw.designSystem.createdAt),
					updatedAt: new Date(raw.designSystem.updatedAt),
				},
				linkedVersion: processVersion(raw.linkedVersion),
				latestPublishedVersion: processVersion(raw.latestPublishedVersion),
				hasDraft: raw.hasDraft,
			};
			store.dispatch(updateActiveSceneDsLink(dsLink));
		} else {
			store.dispatch(updateActiveSceneDsLink(null));
		}

		// Update modes (from linked DS config/display.json)
		if (response.dsModes && response.dsModes.length > 0) {
			store.dispatch(updateActiveSceneDesignSystemModes(response.dsModes));
		} else {
			store.dispatch(updateActiveSceneDesignSystemModes(null));
		}
	}
}
